import React, { useState } from 'react';
import {
  ArrowLeft, Settings, Clock, BookOpen, Edit3, Play, BarChart3, 
  Users, Target, Award, CheckCircle, AlertCircle, PlusCircle,
  FileText, Calendar, User, Trash2, Copy
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TestStartPage = () => {
  // Simulate different test states - change this to test different scenarios
  const [hasQuestions, setHasQuestions] = useState(false); // Set to true to see the "has questions" version
  const navigate = useNavigate();
  
  const testData = {
    id: 1,
    name: "Scenario-Based EAP Test 2",
    description: "Comprehensive English assessment focusing on academic scenarios",
    timeLimit: "2 giờ 10 phút",
    gradingMethod: "Highest grade",
    scenarios: [
      { id: 1, name: "Scenario 1", description: "Applying for a part-time job" },
      { id: 2, name: "Scenario 2", description: "Reviewing academic studies for essay writing" }
    ],
    attempts: hasQuestions ? [
      {
        id: 1,
        state: "Finished",
        submittedDate: "Friday, 8 August 2025, 12:59 AM",
        marks: "3.00 / 34.00",
        grade: "0.88 / 10.00"
      }
    ] : [],
    highestGrade: hasQuestions ? "0.88 / 10.00" : null,
    status: hasQuestions ? "active" : "draft"
  };

  const EmptyTestView = () => (
    <div className="space-y-6">
      {/* Test Header */}
      <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-600">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{testData.name}</h1>
                <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                  Nháp
                </span>
              </div>
            </div>
            <p className="text-gray-600 mb-4">{testData.description}</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2">
            <button className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200">
              <Edit3 className="w-4 h-4 mr-2" />
              Chỉnh sửa bài test
            </button>
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200">
              <Settings className="w-4 h-4 mr-2" />
              Cài đặt
            </button>
          </div>
        </div>
      </div>

      {/* Test Configuration */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center mb-4">
            <Clock className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Thời gian làm bài</h3>
          </div>
          <p className="text-2xl font-bold text-blue-600 mb-2">{testData.timeLimit}</p>
          <p className="text-sm text-gray-500">Giới hạn thời gian cho bài test</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center mb-4">
            <BarChart3 className="w-5 h-5 text-green-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Phương thức chấm điểm</h3>
          </div>
          <p className="text-2xl font-bold text-green-600 mb-2">{testData.gradingMethod}</p>
          <p className="text-sm text-gray-500">Lấy điểm cao nhất từ các lần làm</p>
        </div>
      </div>

      {/* Empty State */}
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="text-center max-w-md mx-auto">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Chưa có câu hỏi nào được thêm</h3>
          <p className="text-gray-600 mb-6">Bài test này chưa có nội dung. Hãy thêm câu hỏi để sinh viên có thể làm bài.</p>
          
          <button 
            onClick={() => navigate('/test/add_question')}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
          >
            <PlusCircle className="w-5 h-5 mr-2" />
            Thêm câu hỏi
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 pt-4">
        <button className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Quay lại danh sách
        </button>
        <button className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200">
          <Copy className="w-4 h-4 mr-2" />
          Sao chép bài test
        </button>
        <button className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors duration-200">
          <Trash2 className="w-4 h-4 mr-2" />
          Xóa bài test
        </button>
      </div>
    </div>
  );

  const TestWithQuestionsView = () => (
    <div className="space-y-6">
      {/* Test Header */}
      <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-600">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{testData.name}</h1>
                <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                  Hoạt động
                </span>
              </div>
            </div>
            <p className="text-gray-600 mb-4">{testData.description}</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2">
            <button className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200">
              <Edit3 className="w-4 h-4 mr-2" />
              Chỉnh sửa câu hỏi
            </button>
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200">
              <Settings className="w-4 h-4 mr-2" />
              Cài đặt
            </button>
          </div>
        </div>
      </div>

      {/* Test Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center mb-4">
            <Clock className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Thời gian</h3>
          </div>
          <p className="text-2xl font-bold text-blue-600 mb-1">{testData.timeLimit}</p>
          <p className="text-sm text-gray-500">Giới hạn thời gian</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center mb-4">
            <BarChart3 className="w-5 h-5 text-green-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Chấm điểm</h3>
          </div>
          <p className="text-2xl font-bold text-green-600 mb-1">{testData.gradingMethod}</p>
          <p className="text-sm text-gray-500">Phương thức chấm</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center mb-4">
            <Target className="w-5 h-5 text-purple-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Điểm cao nhất</h3>
          </div>
          <p className="text-2xl font-bold text-purple-600 mb-1">{testData.highestGrade}</p>
          <p className="text-sm text-gray-500">Từ các lần làm</p>
        </div>
      </div>

      {/* Scenarios */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center mb-6">
          <BookOpen className="w-5 h-5 text-blue-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Kịch bản bài test (130 phút)</h3>
        </div>
        
        <div className="space-y-3">
          {testData.scenarios.map((scenario, index) => (
            <div key={scenario.id} className="flex items-center p-4 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                  {index + 1}
                </div>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-900">{scenario.name}:</p>
                <p className="text-sm text-gray-600">{scenario.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Previous Attempts */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center mb-6">
          <BarChart3 className="w-5 h-5 text-green-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Tổng kết các lần làm trước đây</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lần thử</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trạng thái</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Điểm / 34.00</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Điểm / 10.00</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Xem lại</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {testData.attempts.map((attempt, index) => (
                <tr key={attempt.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">
                      {index === 0 ? 'Preview' : `Lần ${index}`}
                    </span>
                    <div className="text-xs text-gray-500">
                      Nộp bài {attempt.submittedDate}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      {attempt.state}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{attempt.marks}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{attempt.grade}</td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <button className="text-blue-600 hover:text-blue-900 text-sm font-medium">
                      Xem lại
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <div className="flex items-center">
            <Award className="w-5 h-5 text-blue-600 mr-2" />
            <span className="text-sm font-medium text-blue-900">
              Điểm cao nhất: {testData.highestGrade}
            </span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium">
          <Play className="w-5 h-5 mr-2" />
          Làm lại bài test
        </button>
        <button className="flex-1 inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Quay lại khóa học
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button className="p-2 text-gray-400 hover:text-gray-600 mr-4">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">2025 Scenario-Based Language Assessment</h1>
                <div className="flex items-center text-sm text-gray-500 mt-1">
                  <span>Dashboard</span>
                  <span className="mx-2">/</span>
                  <span>My courses</span>
                  <span className="mx-2">/</span>
                  <span>2025 Scenario-Based Language Assessment</span>
                  <span className="mx-2">/</span>
                  <span>General</span>
                  <span className="mx-2">/</span>
                  <span className="text-blue-600">TEST 3</span>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-600">Ms Mai Giang Vien</span>
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Demo Toggle */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-900">Demo Mode:</span>
            <button
              onClick={() => setHasQuestions(!hasQuestions)}
              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
            >
              {hasQuestions ? 'Hiển thị trạng thái "Chưa có câu hỏi"' : 'Hiển thị trạng thái "Có câu hỏi"'}
            </button>
          </div>
        </div>

        {hasQuestions ? <TestWithQuestionsView /> : <EmptyTestView />}
      </main>
    </div>
  );
};

export default TestStartPage;